import { useEffect, useRef, useState } from 'react'
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useQuery } from '@tanstack/react-query'
import {
  CertificateProgramEnrollments,
  CertificateSubscriptionsService,
} from '@mc/api'
import { useCertificateEnrollments } from '@mc/api-hooks'
import { ErrorPage } from '@mc/error-page'
import { useTranslation } from '@mc/i18n'
import { getUserAttributes } from '@mc/next-user-attributes'
import { paths } from '@mc/paths'
import { useUserInfo } from '@mc/user-info'
import { getPageData, PageDataResult } from 'libs/server/page-data'
import { BaseProviders } from 'pages/certificates/common/BaseProviders'
import {
  EssentialsValuePropsExp,
  HeroSectionValuePropsExp,
} from 'pages/certificates/experiments/mccerts-2805_v1_hub_value_prop'
// mccerts-2805_v1_hub_value_prop
import {
  getPromoVariables,
  type PromoVariables,
} from 'pages/certificates/promo/server'
import {
  getShuffledCertificates,
  getLatestCertificates,
  type CertificateInfo,
  type LatestCertificateInfo,
  CertificateSlugProvider,
} from './common'
import { Layout } from './common/Layout'
import { Meta } from './common/Meta'
import {
  PreEnrolledNavigation,
  TrackingRow,
  TrackingRowProvider,
} from './components'
import { BrowseCertificatesCta } from './components/PromoBanner/BrowseCertificatesCta'
import { PromoBanner } from './components/PromoBanner/PromoBanner'
import { ViewCourseBannerCta } from './components/PromoBanner/ViewCourseBannerCta'
import { PageSources } from './events'
import { useSprigTracking } from './hooks'
import { TrackingAttributes, TrackGoldStandardPageViewed } from './tracking'

const BelowTheFold = dynamic(() => import('./BelowTheFold'), {
  ssr: false,
})

const HomepagePromoModal = dynamic(
  () =>
    import('./components/HomepagePromoModal').then(
      (mod) => mod.HomepagePromoModal,
    ),
  { ssr: false },
)

const HomepagePromoBanner = ({
  promoBanner,
  featuredCertificateSlug,
  enrollments,
}: {
  promoBanner?: PromoVariables['banner'] | null
  featuredCertificateSlug?: string | null
  enrollments?: CertificateProgramEnrollments
}) => {
  if (!promoBanner) return null

  // If the promo specified a featured certificate, than this banner is considered specifically
  // for that certificate and will link to it
  const isFeaturedCertificateBanner = !!featuredCertificateSlug

  // We don't want to show a featured certificate banner to users who have already enrolled in the
  // featured certificate
  const showFeaturedCertificate =
    featuredCertificateSlug &&
    !enrollments?.enrollments?.find(
      (enrollment) =>
        enrollment.certificateProgram?.slug === featuredCertificateSlug,
    )

  // If this is a featured certificate banner and the user is already enrolled, show no banner
  if (isFeaturedCertificateBanner && !showFeaturedCertificate) return null

  const cta = isFeaturedCertificateBanner ? (
    <ViewCourseBannerCta
      certSlug={featuredCertificateSlug}
      kind={promoBanner.buttonKind}
      arrowTheme={promoBanner.buttonArrowTheme}
    />
  ) : (
    <BrowseCertificatesCta
      kind={promoBanner.buttonKind}
      arrowTheme={promoBanner.buttonArrowTheme}
    />
  )

  return (
    <TrackingAttributes location='promo banner'>
      <PromoBanner banner={promoBanner} cta={cta} />
    </TrackingAttributes>
  )
}

export interface CertificatesHubProps {
  certificates: CertificateInfo[]
  userIsLoggedIn: boolean
  userId: string
  aapUser?: boolean
  promoBanner?: PromoVariables['banner'] | null
  featuredCertificateSlug?: string | null
  latestCertificates: LatestCertificateInfo[]
}

export const CertificatesHubImpl = ({
  certificates,
  latestCertificates,
  userIsLoggedIn,
  userId,
  aapUser,
  promoBanner,
  featuredCertificateSlug,
}: CertificatesHubProps) => {
  const { t } = useTranslation('@mc/certificates')
  const router = useRouter()

  const { user } = useUserInfo()
  const realUserId = user?.id
  const identifiedUserRef = useRef<string | number | null>(null)
  const { identifyUser: identifySprigUser } = useSprigTracking()

  useEffect(() => {
    const currentUserId = realUserId || userId

    if (currentUserId && identifiedUserRef.current !== currentUserId) {
      // Identify with Sprig
      identifySprigUser(currentUserId.toString())
      identifiedUserRef.current = currentUserId
    }
  }, [userId, realUserId, identifySprigUser])

  // Performance note:
  // This marketing page will receive considerable traffic, and ads will point to it.
  // For this reason, user-related information requests should never block page load for logged-out users, who make up the majority of the traffic, that's why this request is enabled only for logged-in users.
  const [isCheckingEnrollment, setIsCheckingEnrollment] =
    useState(userIsLoggedIn)
  // Expired enrollments are included so the push condition below stays in
  // lockstep with the homepage hub's admit condition — otherwise an expired-only
  // learner bounces between the two pages.
  const { data: enrollments, isFetched: enrollmentsFetched } =
    useCertificateEnrollments(
      {
        active: true,
        pending: true,
        expired: true,
      },
      {
        refetchOnWindowFocus: false,
        enabled: !!userIsLoggedIn,
      },
    )

  // A subscriber with zero enrollments still belongs on the logged-in hub
  const { data: mySubscription, isFetched: subscriptionFetched } = useQuery(
    ['my-certificate-subscription'],
    () => CertificateSubscriptionsService.getMyCertificateSubscription(),
    {
      refetchOnWindowFocus: false,
      enabled: !!userIsLoggedIn,
    },
  )

  useEffect(() => {
    if (!userIsLoggedIn) return

    // Redirect to the hub on subscribed, enrolled or expired
    if (mySubscription?.isSubscribed || enrollments?.enrollments?.length) {
      router.push({
        pathname: paths.certificatesHomepage,
        query: router.query,
      })
      return
    }

    // Only conclude "neither subscribed nor enrolled" once both have resolved.
    if (enrollmentsFetched && subscriptionFetched) {
      setIsCheckingEnrollment(false)
    }
  }, [
    userIsLoggedIn,
    enrollmentsFetched,
    subscriptionFetched,
    enrollments,
    mySubscription,
    router,
  ])

  if (isCheckingEnrollment) return null

  const KEY_BENEFITS = [
    {
      title: t('marketingPages.hub.essentials.1.title'),
      description: t('marketingPages.hub.essentials.1.description'),
    },
    {
      title: t('marketingPages.hub.essentials.2.title'),
      description: t('marketingPages.hub.essentials.2.description'),
    },
    {
      title: t('marketingPages.hub.essentials.3.title'),
      description: t('marketingPages.hub.essentials.3.description'),
    },
    {
      title: t('marketingPages.hub.essentials.4.title'),
      description: t('marketingPages.hub.essentials.4.description'),
    },
  ]

  return (
    <>
      <Meta
        title={t('marketingPages.hub.meta.title')}
        description={t('marketingPages.hub.meta.description')}
        image='https://static.masterclass.com/certs-hero-fallback-1x1.jpg'
      />
      <TrackGoldStandardPageViewed
        name='Certificate Hub Landing'
        product='certificates'
      />
      <TrackingAttributes page_source='Certificate Hub Landing'>
        <HomepagePromoModal />
        <CertificateSlugProvider
          value={{ certSlug: latestCertificates[0].slug }}
        >
          <Layout backgroundColor='var(--c-beige-600)'>
            <TrackingRowProvider event='certificateRowViewed'>
              <TrackingAttributes location='nav bar'>
                <PreEnrolledNavigation
                  topics={certificates}
                  redirectTo={paths.root}
                  latestCertificates={latestCertificates}
                  aapUser={aapUser}
                />
              </TrackingAttributes>
              <HomepagePromoBanner
                promoBanner={promoBanner}
                featuredCertificateSlug={featuredCertificateSlug}
                enrollments={enrollments}
              />
              <div className='bg-c-base-600 flex flex-col'>
                <TrackingRow row_name='hero'>
                  {/* mccerts-2805_v1_hub_value_prop */}
                  <HeroSectionValuePropsExp />
                </TrackingRow>
                <TrackingRow row_name='essentials'>
                  {/* mccerts-2805_v1_hub_value_prop */}
                  <EssentialsValuePropsExp keyBenefits={KEY_BENEFITS} />
                </TrackingRow>
                <div className='mb-9' />
              </div>
              <BelowTheFold
                enrollments={enrollments}
                isLoggedIn={userIsLoggedIn}
              />
            </TrackingRowProvider>
          </Layout>
        </CertificateSlugProvider>
      </TrackingAttributes>
    </>
  )
}

const CertificatesHub = (
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
) => {
  if (props.status === 'error') {
    return <ErrorPage />
  }

  return (
    <BaseProviders>
      <CertificatesHubImpl {...props} />
    </BaseProviders>
  )
}

CertificatesHub.pageSettings = {
  title: 'MasterClass Certificates',
  rumLabel: PageSources.CERTIFICATE_HUB_LANDING,
  navigation: null,
  footer: null,
}

export const getServerSideProps: GetServerSideProps<
  PageDataResult<CertificatesHubProps>
> = async (ctx: GetServerSidePropsContext) => {
  const userAttributes = getUserAttributes(ctx, { query: ctx.query })
  const certificates = getShuffledCertificates(ctx)
  const latestCertificates = getLatestCertificates()

  // Get homepage promo variables
  const homepagePromoVariables = await getPromoVariables(ctx, {
    promoSurface: 'homepage',
  })
  let featuredCertPromoVariables: PromoVariables | null = null

  // If we have a featured certificate, get its promo variables. Note this call
  // resolves site-wide-first as well, so an active `certificates-promotions`
  // payload supplies the banner here and `mccerts-promo-{slug}` is not consulted.
  //
  // `featured_certificate` rides the same site-wide win as the banner: when the
  // site-wide payload is authored in the cert-only shape it strips to `{}` under
  // HomepagePromoVariablesSchema, so both this block and `featuredCertificateSlug`
  // below fall to null and the homepage renders no banner from either source. To
  // keep them during a site-wide promo the payload needs top-level
  // `featured_certificate` + `banner` (both live in the homepage schema) — plus
  // `promo`, which the homepage schema doesn't require but the cert schema below
  // does: the featured-cert read re-parses this same payload under
  // CertificatePromoVariablesSchema, so a payload missing `promo` reports a parse
  // error on every homepage SSR and falls back to `mccerts-promo-{slug}`.
  if (homepagePromoVariables?.featured_certificate) {
    featuredCertPromoVariables = await getPromoVariables(ctx, {
      promoSurface: 'cert',
      slug: homepagePromoVariables.featured_certificate,
    })
  }

  // display homepage banner if present, otherwise use featured certificate banner
  const promoBanner =
    homepagePromoVariables?.banner ?? featuredCertPromoVariables?.banner ?? null

  return getPageData({
    propsMapper: (props) => ({
      certificates,
      latestCertificates,
      userIsLoggedIn: userAttributes.is_logged_in,
      userId: userAttributes.userId,
      aapUser: userAttributes.membership === 'AAP',
      promoBanner,
      featuredCertificateSlug:
        homepagePromoVariables?.featured_certificate ?? null,
      ...props,
    }),
  })
}

export default CertificatesHub
